import { initializeApp } from 'firebase/app';
import {
  getAuth,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: 'G-GKDNNVYKPF',
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Initialize Firebase Analytics (conditionally - not supported in all environments)
import { initAnalytics } from './analytics';

export let firebaseAnalytics: Analytics | null = null;
isSupported().then((supported) => {
  if (supported) {
    firebaseAnalytics = getAnalytics(app);
    initAnalytics(firebaseAnalytics);
  }
});

// Email for magic link - stored in localStorage to complete sign-in
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

export const firebaseAuth = {
  // Send passwordless magic link to email
  sendSignInLink: async (email: string) => {
    const actionCodeSettings = {
      url: `${window.location.origin}/auth/callback`,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save email locally to complete sign-in when user clicks link
    localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
  },

  // Check if current URL is a sign-in link
  isSignInWithEmailLink: (url: string) => isSignInWithEmailLink(auth, url),

  // Complete sign-in from magic link
  completeSignInWithEmailLink: async (url: string, email?: string) => {
    const storedEmail = email || localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
    if (!storedEmail) {
      throw new Error('Email not found. Please enter your email again.');
    }
    const result = await signInWithEmailLink(auth, storedEmail, url);
    localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
    return result;
  },

  // Get stored email for sign-in link
  getStoredEmail: () => localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY),

  signInWithGoogle: () => signInWithPopup(auth, googleProvider),

  signOut: () => signOut(auth),

  onAuthChange: (callback: (user: User | null) => void) =>
    onAuthStateChanged(auth, callback),

  getCurrentUser: () => auth.currentUser,

  getIdToken: async () => {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  },
};

export type { User };
